package ru.dinarastepina.fabvocab.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.forms.TextInput
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import ru.dinarastepina.fabvocab.contracts.ExerciseDto
import ru.dinarastepina.fabvocab.web.api.VocabularyApiClient
import ru.dinarastepina.fabvocab.web.components.EmptyState
import ru.dinarastepina.fabvocab.web.components.FeedbackCard
import ru.dinarastepina.fabvocab.web.components.ProgressBar
import ru.dinarastepina.fabvocab.web.components.TopBar
import ru.dinarastepina.fabvocab.web.theme.*

@Page("practice")
@Composable
fun PracticePage() {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val client = remember { VocabularyApiClient() }
    val exercises = remember { mutableStateOf<List<ExerciseDto>>(emptyList()) }
    val currentIndex = remember { mutableStateOf(0) }
    val answer = remember { mutableStateOf("") }
    val feedback = remember { mutableStateOf<String?>(null) }
    val isCorrect = remember { mutableStateOf(false) }
    val error = remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        try {
            exercises.value = client.getExercises()
        } catch (e: Exception) {
            error.value = e.message
        }
    }

    Column(modifier = Modifier.fillMaxWidth().gap(16.px)) {
        TopBar(
            title = "Practice",
            navigation = {
                Button(
                    onClick = { ctx.router.navigateTo("/") },
                    colorPalette = SlatePalette,
                    size = ButtonSize.XS,
                    modifier = Modifier.margin(right = 8.px),
                ) {
                    SpanText("← Back")
                }
            },
        )

        val current = exercises.value.getOrNull(currentIndex.value)

        if (current != null) {
            val progress = if (exercises.value.isNotEmpty()) {
                (currentIndex.value + 1).toFloat() / exercises.value.size
            } else 0f

            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = com.varabyte.kobweb.compose.foundation.layout.Arrangement.SpaceBetween,
            ) {
                SpanText(
                    "${currentIndex.value + 1} / ${exercises.value.size}",
                    modifier = Modifier
                        .fontSize(10.px)
                        .fontWeight(FontWeight.Bold)
                        .color(Slate400)
                        .letterSpacing(0.5.px),
                )
                Box(
                    modifier = Modifier
                        .backgroundColor(Indigo50)
                        .borderRadius(6.px)
                        .padding(leftRight = 8.px, topBottom = 2.px),
                ) {
                    SpanText(
                        "Drill",
                        modifier = Modifier
                            .fontSize(10.px)
                            .fontWeight(FontWeight.Bold)
                            .color(Indigo600)
                            .letterSpacing(0.5.px),
                    )
                }
            }

            ProgressBar(progress = progress)

            when (current) {
                is ExerciseDto.FillBlankDto -> {
                    ExerciseCard(title = "Fill in the blank") {
                        Column(modifier = Modifier.fillMaxWidth().gap(12.px)) {
                            SpanText(
                                current.sentence,
                                modifier = Modifier
                                    .fontSize(16.px)
                                    .fontWeight(FontWeight.Bold)
                                    .color(Slate800)
                                    .lineHeight(1.5),
                            )
                            current.hint?.let {
                                SpanText(
                                    "Hint: $it",
                                    modifier = Modifier
                                        .fontSize(12.px)
                                        .color(Slate500),
                                )
                            }
                            TextInput(
                                text = answer.value,
                                onTextChange = { answer.value = it },
                                modifier = Modifier.fillMaxWidth(),
                                placeholder = "Your answer...",
                                focusBorderColor = Indigo600,
                            )
                            Button(
                                onClick = {
                                    scope.launch {
                                        val correct = answer.value.trim().lowercase() == current.answer.trim().lowercase()
                                        isCorrect.value = correct
                                        feedback.value = if (correct) "Correct!" else "Wrong! Correct answer: ${current.answer}"
                                        client.recordExerciseResult(
                                            current.wordId,
                                            "fill_blank",
                                            correct
                                        )
                                    }
                                },
                                enabled = answer.value.isNotBlank(),
                                colorPalette = IndigoPalette,
                                size = ButtonSize.SM,
                                modifier = Modifier.fillMaxWidth(),
                            ) {
                                SpanText("Check", modifier = Modifier.fontWeight(FontWeight.Bold))
                            }
                        }
                    }
                }
                is ExerciseDto.DefinitionMatchDto -> {
                    ExerciseCard(title = "Definition match") {
                        Column(modifier = Modifier.fillMaxWidth().gap(12.px)) {
                            SpanText(
                                "Choose the definition for",
                                modifier = Modifier
                                    .fontSize(14.px)
                                    .color(Slate500),
                            )
                            SpanText(
                                current.word,
                                modifier = Modifier
                                    .fontSize(20.px)
                                    .fontWeight(FontWeight.Black)
                                    .color(Slate800),
                            )
                            Column(modifier = Modifier.fillMaxWidth().gap(8.px)) {
                                current.options.forEachIndexed { idx, opt ->
                                    Button(
                                        onClick = {
                                            scope.launch {
                                                val correct = idx == current.correctIndex
                                                isCorrect.value = correct
                                                feedback.value = if (correct) "Correct!" else "Wrong!"
                                                client.recordExerciseResult(
                                                    current.wordId,
                                                    "definition_match",
                                                    correct
                                                )
                                            }
                                        },
                                        colorPalette = SlatePalette,
                                        size = ButtonSize.SM,
                                        modifier = Modifier.fillMaxWidth(),
                                    ) {
                                        SpanText(opt, modifier = Modifier.fontWeight(FontWeight.SemiBold))
                                    }
                                }
                            }
                        }
                    }
                }
                is ExerciseDto.TrueFalseDto -> {
                    ExerciseCard(title = "True or False") {
                        Column(modifier = Modifier.fillMaxWidth().gap(12.px)) {
                            SpanText(
                                current.sentence,
                                modifier = Modifier
                                    .fontSize(16.px)
                                    .fontWeight(FontWeight.Bold)
                                    .color(Slate800)
                                    .lineHeight(1.5),
                            )
                            Row(modifier = Modifier.fillMaxWidth().gap(8.px)) {
                                Button(
                                    onClick = {
                                        scope.launch {
                                            val correct = current.isCorrectUsage
                                            isCorrect.value = correct
                                            feedback.value = if (correct) "Correct!" else "Wrong!"
                                            client.recordExerciseResult(
                                                current.wordId,
                                                "true_false",
                                                correct
                                            )
                                        }
                                    },
                                    colorPalette = EmeraldPalette,
                                    size = ButtonSize.SM,
                                    modifier = Modifier.weight(1f),
                                ) {
                                    SpanText("True", modifier = Modifier.fontWeight(FontWeight.Bold))
                                }
                                Button(
                                    onClick = {
                                        scope.launch {
                                            val correct = !current.isCorrectUsage
                                            isCorrect.value = correct
                                            feedback.value = if (correct) "Correct!" else "Wrong!"
                                            client.recordExerciseResult(
                                                current.wordId,
                                                "true_false",
                                                correct
                                            )
                                        }
                                    },
                                    colorPalette = RosePalette,
                                    size = ButtonSize.SM,
                                    modifier = Modifier.weight(1f),
                                ) {
                                    SpanText("False", modifier = Modifier.fontWeight(FontWeight.Bold))
                                }
                            }
                            current.explanation?.let {
                                SpanText(
                                    it,
                                    modifier = Modifier
                                        .fontSize(12.px)
                                        .color(Slate500),
                                )
                            }
                        }
                    }
                }
            }

            feedback.value?.let {
                FeedbackCard(message = it, isError = !isCorrect.value)
                Button(
                    onClick = {
                        answer.value = ""
                        feedback.value = null
                        currentIndex.value = (currentIndex.value + 1) % exercises.value.size
                    },
                    colorPalette = IndigoPalette,
                    size = ButtonSize.SM,
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    SpanText(
                        if (currentIndex.value >= exercises.value.size - 1) "🏁 Finish" else "Next →",
                        modifier = Modifier.fontWeight(FontWeight.Bold),
                    )
                }
            }
        } else {
            if (error.value != null) {
                FeedbackCard(message = error.value!!, isError = true)
            } else {
                EmptyState(
                    title = "Not enough words yet",
                    subtitle = "Save a few more words with definitions and examples to start practicing.",
                    modifier = Modifier.padding(top = 48.px),
                )
            }
        }
    }
}

@Composable
private fun ExerciseCard(
    title: String,
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) {
    Box(
        modifier = modifier
            .fillMaxWidth()
            .backgroundColor(Color("#FFFFFF"))
            .border(2.px, LineStyle.Solid, Slate100)
            .borderRadius(24.px)
            .padding(20.px),
    ) {
        Column(modifier = Modifier.fillMaxWidth().gap(12.px)) {
            Box(
                modifier = Modifier
                    .backgroundColor(Indigo50)
                    .borderRadius(6.px)
                    .padding(leftRight = 8.px, topBottom = 2.px),
            ) {
                SpanText(
                    title,
                    modifier = Modifier
                        .fontSize(10.px)
                        .fontWeight(FontWeight.Bold)
                        .color(Indigo600)
                        .letterSpacing(0.5.px),
                )
            }
            content()
        }
    }
}
